import React, {useEffect, useState} from "react";
import {Button, Col, Form, ListGroup, Row, Spinner} from "react-bootstrap";
import {useCanvasDataSources} from "@/hooks/canvas";
import {EAIClient} from "Services/EAIClient";
import {useAppContext} from "@/contexts/app";
import {Utils} from "../../common/utils";
import {useSearchParams} from "react-router-dom";
import PageTitle from "Components/Common/PageTitle";
import { Eye, EyeOff, Copy } from "lucide-react";

/**
 * Human-readable form of the authorize URL, for display only.
 *
 * The ~15 Canvas scopes are space-joined and percent-encoded into a single
 * unbroken ~1,000-character query token (`scope=url%3AGET%7C%2Fapi%2Fv1%2F...`),
 * which is unreadable on screen. Decoding restores the literal Canvas scope
 * syntax (`scope=url:GET|/api/v1/accounts url:GET|...`), which also gives the
 * browser real spaces to wrap on instead of breaking mid-token.
 *
 * The anchor's href always keeps the exact encoded URL — only the visible label
 * is decoded — so following or copying the link sends what Canvas expects.
 */
const formatAuthorizeUrlForDisplay = (value: string): string => {
    if (!value) return "";
    try {
        // "+" means a space in a query string; decodeURIComponent leaves it as-is.
        return decodeURIComponent(value.replace(/\+/g, " "));
    } catch {
        // Malformed escape sequence — fall back to showing the URL verbatim.
        return value;
    }
};

export default function CanvasPage() {
    const {data: canvasDatasource, error, mutate} = useCanvasDataSources();
    const [clientId, setClientId] = useState("");
    const [clientKey, setClientKey] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const client = new EAIClient();
    const {dispatchError, addAlert} = useAppContext();

    const [showClientId, setShowClientId] = useState(false);
    const [showClientKey, setShowClientKey] = useState(false);

    const LEFT_COL_WIDTH = 3;
    const RIGHT_COL_WIDTH = 12 - LEFT_COL_WIDTH;

    const save = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitting(true);
        const data = {
            settings: {
                client_id: clientId,
                client_secret: clientKey,
                base_url: baseUrl.trim()
            }
        };
        try {
            await client.updateCanvasDatasource(data);
            addAlert("Save Complete");
            await mutate();
        } catch (e) {
            dispatchError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            addAlert("Copied to clipboard!");
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    useEffect(() => {
        async function initialize() {
            Utils.setViewName("CanvasConfiguration", "Canvas");

            if (searchParams.get("error")) {
                let errorText = searchParams.get("error") as string;
                if (searchParams.get("error_description")) {
                    errorText = searchParams.get("error_description") as string;
                }
                dispatchError(new Error("Canvas Error"), errorText);
            } else if (searchParams.get("code")) {
                const data = {
                    settings: {
                        code: searchParams.get("code") as string
                    }
                };
                try {
                    await client.updateCanvasDatasource(data);
                    addAlert("Canvas Configuration Complete");
                    searchParams.delete("code");
                    setSearchParams(searchParams.toString());
                    await mutate();
                } catch (e) {
                    dispatchError(e);
                }
            }
        }
        initialize();
    }, []);

    useEffect(() => {
        if (canvasDatasource) {
            setClientId(canvasDatasource.data.settings.client_id || "");
            setClientKey(canvasDatasource.data.settings.client_secret || "");
            setBaseUrl(canvasDatasource.data.settings.base_url || "");
        }
    }, [canvasDatasource]);

    if (error) {
        return <div className="p-3 text-center">Error loading data: {error.message}</div>;
    }

    if (!canvasDatasource) {
        return <div className="p-3 text-center"><Spinner variant="success" /></div>;
    }

    return (
        <>
            <PageTitle pageTitle={"Canvas Configuration"} />
            <Row className="section-action-bar">
                <Col md={12} lg={6} className="title">
                    <h3>Canvas Configuration</h3>
                </Col>
            </Row>
            <div>
                <div className="row-padding-bottom">
                    <h3>Step 1:</h3>
                    <a target="_blank" rel="noopener noreferrer"
                       href="https://knowledgebase.enflux.com/en/articles/5530618-how-do-i-give-enflux-access-to-canvas">Create
                        a Canvas User</a>
                </div>

                <div className="row-padding-bottom">
                    <h3>Step 2:</h3>
                    <span>
                            Copy the following to the Canvas Developer Key settings (<a target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        href="http://knowledgebase.enflux.com/en/articles/5964449-canvas-create-api-developer-key">How do I add a developer key?</a>):
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Key Name:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    Enflux
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Owner Email:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    support@enflux.com
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Redirect URIs:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    {canvasDatasource.data.settings.redirect_uris}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Redirect URI (Legacy):</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    {canvasDatasource.data.settings.redirect_uri_legacy}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Vendor Code (LTI 2):</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    Leave Blank
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Icon URL:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    {canvasDatasource.data.settings.icon_url}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={LEFT_COL_WIDTH}>
                                    <strong>Client Credentials Audience:</strong>
                                </Col>
                                <Col md={RIGHT_COL_WIDTH}>
                                    Canvas
                                </Col>
                            </Row>
                        </span>
                    <Row className="row-padding-top">
                        <Col>
                            <h5>Optional</h5> <a target="_blank" rel="noopener noreferrer"
                                                 href="https://knowledgebase.enflux.com/en/articles/5998763-enabling-scope-on-canvas-developer-keys-optional">Enable
                            Scope on Keys</a>
                        </Col>
                    </Row>
                </div>
                <div className="row-padding-bottom">
                    <h3>Step 3:</h3>
                    <Row className="row-padding-bottom">
                        <Col>
                            Copy the Client ID (it will look like <em>157060000000000201</em>) and Key (click &quot;Show
                            Key&quot;, it will look
                            like <em>AKdIRM4ISlczp5cDzB2ZUlxN56uKSGqeF0xF20sbiteJT9xHA7UcIN5IsLnTibVe</em>) from
                            Canvas to the fields below.<br/>
                        </Col>
                    </Row>
                    <Form onSubmit={save}>
                        <Form.Group controlId="formClientID" className="mb-3 position-relative">
                            <Form.Label column="lg">Client ID</Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    type={showClientId ? "text" : "password"}
                                    placeholder="Enter Client ID"
                                    onChange={(ev) => setClientId(ev.target.value)}
                                    value={clientId}
                                    disabled={canvasDatasource.data.settings.ui_locked === "true"}
                                />
                                <div
                                    className="d-flex gap-2 align-items-center position-absolute end-0 top-0 h-100 pe-2"
                                    style={{ cursor: "pointer" }}
                                >
                                    {showClientId ? (
                                        <EyeOff onClick={() => setShowClientId(false)} className="text-muted" />
                                    ) : (
                                        <Eye onClick={() => setShowClientId(true)} className="text-muted" />
                                    )}
                                    <Copy onClick={() => handleCopy(clientId)} className="text-muted" />
                                </div>
                            </div>
                        </Form.Group>
                        <Form.Group controlId="formClientKey" className="mb-3 position-relative">
                            <Form.Label column="lg">Client Key</Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    type={showClientKey ? "text" : "password"}
                                    placeholder="Enter Client Key"
                                    onChange={(ev) => setClientKey(ev.target.value)}
                                    value={clientKey}
                                    disabled={canvasDatasource.data.settings.ui_locked === "true"}
                                />
                                <div
                                    className="d-flex gap-2 align-items-center position-absolute end-0 top-0 h-100 pe-2"
                                    style={{ cursor: "pointer" }}
                                >
                                    {showClientKey ? (
                                        <EyeOff onClick={() => setShowClientKey(false)} className="text-muted" />
                                    ) : (
                                        <Eye onClick={() => setShowClientKey(true)} className="text-muted" />
                                    )}
                                    <Copy onClick={() => handleCopy(clientKey)} className="text-muted" />
                                </div>
                            </div>
                        </Form.Group>
                        <Form.Group controlId="formBaseUrl" className="mb-3 position-relative">
                            <Form.Label column="lg">Base URL (optional)</Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Base URL (e.g. https://canvas.example.edu)"
                                    onChange={(ev) => setBaseUrl(ev.target.value)}
                                    value={baseUrl}
                                    disabled={canvasDatasource.data.settings.ui_locked === "true"}
                                />
                                <div
                                    className="d-flex gap-2 align-items-center position-absolute end-0 top-0 h-100 pe-2"
                                    style={{ cursor: "pointer" }}
                                >
                                    <Copy onClick={() => handleCopy(baseUrl)} className="text-muted" />
                                </div>
                            </div>
                        </Form.Group>
                        {submitting
                            ? <Spinner animation="border" size="sm"/>
                            : (
                                <p>
                                    <Button type="submit" disabled={canvasDatasource.data.settings.ui_locked === "true"}>Save</Button>
                                    {canvasDatasource.data.settings.ui_locked === "true" &&
                                        <span className="ms-2 text-warning">This configuration is locked. Please contact Enflux support to unlock.</span>}
                                </p>
                            )
                        }
                    </Form>
                </div>
                <div className="row-padding-bottom">
                    <h3>Step 4:</h3>
                    <span>
                            <ListGroup as="ol">
                                <ListGroup.Item as="li">Log out of Canvas</ListGroup.Item>
                                <ListGroup.Item
                                    as="li">Log in to the Enflux Canvas account created in Step 1</ListGroup.Item>
                                <ListGroup.Item as="li">
                                    <div className="d-flex align-items-start gap-2">
                                        <span>
                                            Follow this URL and accept the request to give Enflux access:<br/>
                                            <a className="text-break" href={canvasDatasource.data.settings.authorize_url}>
                                                {formatAuthorizeUrlForDisplay(canvasDatasource.data.settings.authorize_url)}
                                            </a>
                                        </span>
                                        <Copy
                                            onClick={() => handleCopy(canvasDatasource.data.settings.authorize_url)}
                                            className="text-muted flex-shrink-0"
                                            style={{ cursor: "pointer" }}
                                        />
                                    </div>
                                </ListGroup.Item>
                            </ListGroup>
                        </span>
                </div>
            </div>
        </>
    );
}
